import axios, { AxiosInstance } from 'axios';
import { LoginUserData } from '../components/Login';

// Auth API types
export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  name: string;
  email: string;
  password: string;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface AuthConfig {
  baseURL: string;
  tokenKey?: string;
  userKey?: string;
  onUnauthorized?: () => void;
}

export class AuthApiClient {
  private client: AxiosInstance;
  private tokenKey: string;
  private userKey: string;
  private onUnauthorized?: () => void;

  constructor(config: AuthConfig) {
    const { baseURL, tokenKey = 'accessToken', userKey = 'user', onUnauthorized } = config;
    
    this.tokenKey = tokenKey;
    this.userKey = userKey;
    this.onUnauthorized = onUnauthorized;

    this.client = axios.create({
      baseURL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem(this.tokenKey);
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle errors
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Clear tokens
          localStorage.removeItem(this.tokenKey);
          localStorage.removeItem(this.userKey);
          localStorage.removeItem('refreshToken');
          
          // Call unauthorized handler if provided
          if (this.onUnauthorized) {
            this.onUnauthorized();
          } else {
            window.location.reload();
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // Helper method to handle API responses
  private async handleResponse<T>(response: any): Promise<T> {
    const apiResponse: ApiResponse<T> = response.data;
    
    if (!apiResponse.success) {
      throw new Error(apiResponse.error || apiResponse.message || 'API request failed');
    }
    
    return apiResponse.data as T;
  }

  // Login
  async login(credentials: LoginRequest): Promise<LoginUserData> {
    try {
      const response = await this.client.post('/api/auth/login', credentials);
      return await this.handleResponse<LoginUserData>(response);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || error.response?.data?.message || 'Login failed');
    }
  }

  // Register
  async register(userData: RegisterRequest): Promise<LoginUserData> {
    try {
      const response = await this.client.post('/api/auth/register', userData);
      return await this.handleResponse<LoginUserData>(response);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || error.response?.data?.message || 'Registration failed');
    }
  }

  // Google OAuth
  async googleAuth(credential: string): Promise<LoginUserData> {
    try {
      const response = await this.client.post('/api/auth/google', { credential });
      return await this.handleResponse<LoginUserData>(response);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || error.response?.data?.message || 'Google login failed');
    }
  }

  // Verify token
  async verifyToken(): Promise<LoginUserData> {
    try {
      const response = await this.client.get('/api/auth/verify');
      return await this.handleResponse<LoginUserData>(response);
    } catch (error: any) {
      throw new Error('Token verification failed');
    }
  }

  // Forgot password
  async forgotPassword(email: string): Promise<{ message: string }> {
    try {
      const response = await this.client.post('/api/auth/forgot-password', { email });
      return await this.handleResponse<{ message: string }>(response);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || error.response?.data?.message || 'Failed to send reset email');
    }
  }

  // Reset password
  async resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
    try {
      const response = await this.client.post('/api/auth/reset-password', { 
        token, 
        password: newPassword 
      });
      return await this.handleResponse<{ message: string }>(response);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || error.response?.data?.message || 'Failed to reset password');
    }
  }

  // Logout
  async logout(): Promise<void> {
    try {
      await this.client.post('/api/auth/logout');
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      // Always clear local storage
      localStorage.removeItem(this.tokenKey);
      localStorage.removeItem(this.userKey);
      localStorage.removeItem('refreshToken');
    }
  }

  // Refresh token
  async refreshToken(): Promise<string> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await this.client.post('/api/auth/refresh', { refreshToken });
      const result = await this.handleResponse<{ accessToken: string }>(response);
      
      // Store new access token
      localStorage.setItem(this.tokenKey, result.accessToken);
      
      return result.accessToken;
    } catch (error: any) {
      throw new Error('Failed to refresh token');
    }
  }
}